import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.appspot.com`,
  messagingSenderId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async (quizCode?: string) => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    if (!user.email || !user.displayName) {
      throw new Error("Missing required user information");
    }

    // Get the ID token
    const idToken = await user.getIdToken();

    // Sync with our backend
    const response = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        email: user.email,
        username: user.displayName,
        firebaseUid: user.uid,
        quizCode,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to sync with backend');
    }

    return user;
  } catch (error) {
    console.error("Error signing in with Google", error);
    if (error instanceof Error) {
      throw new Error(`Google sign-in failed: ${error.message}`);
    }
    throw new Error('Google sign-in failed');
  }
};

export const registerWithEmail = async (email: string, password: string, username: string) => {
  try {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);

    // Sync with our backend
    await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        username,
        firebaseUid: user.uid,
      }),
    });

    return user;
  } catch (error) {
    console.error("Error registering with email", error);
    throw error;
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (error) {
    console.error("Error signing in with email", error);
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
    // Clear backend session
    await fetch('/api/auth/logout', {
      method: 'POST',
    });
  } catch (error) {
    console.error("Error signing out", error);
    throw error;
  }
};

export const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

// Add a new function to get the current user's ID token
export const getCurrentUserToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};